import { useEffect, useRef, useState } from "react";
import { AuthBar } from "./AuthBar";
import * as api from "../lib/api";
import type { MeResult, HubApp, RecentApp } from "../lib/api";

// The public discovery hub, served at "/". No login required. Surfaces:
//   - search across published apps
//   - "Recently played" (from top-origin localStorage written by the app wrapper)
//   - "Starred" (the signed-in/guest user's stars)
//   - a leaderboard of most-used apps (falls back to recently-published)
// Each card links to the app's pretty URL; a "fork" action opens a copy in the Studio.

export function Hub({
  me,
  refreshMe,
  onOpenSettings,
  navigate,
}: {
  me: MeResult | null;
  refreshMe: () => void;
  onOpenSettings: () => void;
  navigate: (to: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HubApp[] | null>(null);
  const [leaders, setLeaders] = useState<HubApp[]>([]);
  const [leaderBasis, setLeaderBasis] = useState<"usage" | "recent">("recent");
  const [starred, setStarred] = useState<HubApp[]>([]);
  const [recent, setRecent] = useState<RecentApp[]>(() => api.recentlyPlayed());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api.hubLeaderboard().then((r) => { setLeaders(r.apps); setLeaderBasis(r.basis); }).catch(() => {});
    api.listMyStars().then((r) => setStarred(r.apps)).catch(() => {});
  }, [me?.userId]);

  // Recently-played lives in localStorage (written by the app wrapper), so it can contain
  // apps that have since been deleted — which the server can't clean. Resolve the stored
  // ids against what's still openable, drop the dead ones, refresh name/path from current
  // data, and write the pruned list back so stale entries don't linger.
  useEffect(() => {
    const stored = api.recentlyPlayed();
    if (stored.length === 0) return;
    api
      .hubResolve(stored.map((r) => r.id))
      .then(({ apps }) => {
        const live = new Map(apps.map((a) => [a.projectId, a]));
        const pruned: RecentApp[] = stored
          .filter((r) => live.has(r.id))
          .map((r) => {
            const a = live.get(r.id)!;
            return { id: r.id, name: a.name, path: `/${a.username}/${a.slug}`, ts: r.ts };
          });
        setRecent(pruned);
        if (pruned.length !== stored.length) api.setRecentlyPlayed(pruned);
      })
      .catch(() => {});
  }, []);

  // Debounced search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      api.hubSearch(q).then((r) => setResults(r.apps)).catch(() => setResults([]));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Note: forking ("Open in Studio") lives in the app's own share menu, not here — you
  // decide to customize after you've actually looked at an app, not while browsing.
  function Card({ app }: { app: HubApp }) {
    return (
      <div className="hub-card">
        <a className="hub-card-main" href={`/${app.username}/${app.slug}`}>
          <div className="hub-card-name">{app.name}</div>
          {app.description && <div className="hub-card-desc">{app.description}</div>}
          <div className="hub-card-meta">
            <span className="hub-card-by">@{app.username}</span>
            {typeof app.uses === "number" && app.uses > 0 && <span className="hub-card-uses">· {app.uses} plays</span>}
            {app.tags.slice(0, 4).map((t) => (
              <span key={t} className="hub-tag" onClick={(e) => { e.preventDefault(); setQuery(t); }}>{t}</span>
            ))}
          </div>
        </a>
      </div>
    );
  }

  function Section({ title, apps }: { title: string; apps: HubApp[] }) {
    if (apps.length === 0) return null;
    return (
      <div className="hub-section">
        <h2 className="hub-section-title">{title}</h2>
        <div className="hub-grid">{apps.map((a) => <Card key={a.projectId} app={a} />)}</div>
      </div>
    );
  }

  return (
    <div className="shell">
      <div className="topbar">
        <div className="title">quikpl.ai</div>
        <div className="spacer" />
        <button className="btn-ghost" onClick={() => navigate("/studio")} title="Build your own app">
          Open Studio
        </button>
        <AuthBar user={me?.user ?? null} onChanged={refreshMe} />
        <button className="btn-ghost" onClick={onOpenSettings} title="Settings">⚙</button>
      </div>
      <div className="hub-body">
        <div className="hub-hero">
          <h1>Find a game to play</h1>
          <p className="hub-sub">Throwaway social micro-apps for parties, tables, and group chats.</p>
          <input
            className="hub-search"
            placeholder="Search apps — trivia, drawing, icebreaker…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        {results !== null ? (
          results.length > 0 ? (
            <Section title={`Results for “${query.trim()}”`} apps={results} />
          ) : (
            <div className="hub-empty">No apps match “{query.trim()}”.</div>
          )
        ) : (
          <>
            {recent.length > 0 && (
              <div className="hub-section">
                <h2 className="hub-section-title">Recently played</h2>
                <div className="hub-grid">
                  {recent.map((r) => (
                    <div className="hub-card" key={r.id}>
                      <a className="hub-card-main" href={r.path}>
                        <div className="hub-card-name">{r.name}</div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Section title="Starred" apps={starred} />
            <Section title={leaderBasis === "usage" ? "Most played this week" : "Recently published"} apps={leaders} />
            {leaders.length === 0 && starred.length === 0 && recent.length === 0 && (
              <div className="hub-empty">
                No published apps yet. <button className="linklike" onClick={() => navigate("/studio")}>Build the first one →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
